import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FoldersService } from '../../services/folders.service';
import { TreeNode } from 'primeng/api';
import { FolderModel } from '../../models/folder-model';
import { TreeNodeContextMenuSelectEvent, TreeNodeSelectEvent } from 'primeng/tree';
import { Router } from '@angular/router';
import { QueryStateService } from '../../services/query-state.service';
import { FormsService } from '../../services/forms.service';
import { ConstantsService } from '../../services/constants.service';
import { FormArray, FormGroup } from '@angular/forms';
import { MetadataProp } from '../../models/metadata-prop-model';
import { MetadataService } from '../../services/metadata.service';
import { SearchComboItemModel } from '../../models/search-combo-item-model';

@Component({
  selector: 'app-folder-view',
  templateUrl: './folder-view.component.html',
  styleUrl: './folder-view.component.css'
})
export class FolderViewComponent implements OnInit, OnChanges {

  @Input() foldersData: FolderModel[] = [];

  searchForm: FormGroup;
  mdItems: MetadataProp[] = [];

  segmentsToExpand: string[] = [];
  currentNodeToExpand: TreeNode | undefined = undefined;
  selectedLib: string;

  statusOptions = [
    { label: "All", icon: "pi pi-list", value: "All" },
    { label: "Open", icon: "fa-solid fa-box-open", value: "Open" },
    { label: "Closed", icon: "fa-solid fa-box", value: "Closed" }
  ];

  selectedStatus: "All" | "Open" | "Closed" = "All";

  contextMenuItems = [
    { label: 'Search Folder', icon: 'pi pi-search', command: (event: TreeNodeContextMenuSelectEvent) => { this.goToAdvSearch(); } }
  ];

  foldersTree!: TreeNode[];

  selectedFolder: TreeNode | undefined;

  constructor(private foldersSvc: FoldersService,
    private router: Router,
    private queryStateSvc: QueryStateService,
    private mdSvc: MetadataService,
    private formsSvc: FormsService,
    private constants: ConstantsService) {

    this.selectedLib = this.constants.indexEHD;
    this.searchForm = this.formsSvc.initSearchForm();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    this.loadFolders();
  }

  ngOnInit(): void {
    this.loadFolders();

    this.initFolderSelection();

    this.mdSvc.mdData$.subscribe((md) => {
      this.mdItems = md;
    });

    this.foldersSvc.selectedLibrary$.subscribe((lib) => {
      this.selectedLib = lib;
    });
  }

  initFolderSelection(fldrPath: string = '') {
    fldrPath = this.foldersSvc.getSelectedFolder();
    fldrPath = this.foldersSvc.ensureRootFolderSlash(fldrPath);

    // Assume EHD root
    let cleanedPath = fldrPath.startsWith(this.constants.base_EHD_folder)
      ? fldrPath.slice(this.constants.base_EHD_folder.length)
      : fldrPath;

    // Check for the SDM root.
    cleanedPath = cleanedPath.startsWith(this.constants.base_SDM_folder)
      ? cleanedPath.slice(this.constants.base_SDM_folder.length)
      : cleanedPath;

    if (cleanedPath) {
      this.segmentsToExpand = cleanedPath.split('/');

      //start from the root
      // NEED to get the entire branch of the tree down to the selected node from the API to then display.
      this.expandNextTreeNode(this.foldersTree);
    }
  }

  expandNextTreeNode(currentNodes: TreeNode[]) {
    const nextSegment = this.segmentsToExpand.shift();

    if (!nextSegment) {
      return;
    }

    const nextNode = currentNodes.find(n => n.label === nextSegment);
    if (!nextNode) {
      console.log(`could not find node for: ${nextSegment}`);
      return;
    }

    nextNode.expanded = true;
    this.currentNodeToExpand = nextNode;
    this.loadSubFolders({ node: nextNode });
  }

  expandAndSelectNode(nodes: TreeNode[], segments: string[]) {
    let currentLevelNodes = nodes;
    let currentPath = '';
    let selected: TreeNode | undefined = undefined;

    for (const segment of segments) {
      const foundNode = currentLevelNodes.find(n => n.label == segment);
      console.log('segment: ' + segment);

      if (!foundNode) {
        console.log(`Could not find Node for segment ${segment}`);
        return;
      }

      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      foundNode.expanded = true;
      selected = foundNode;

      currentLevelNodes = foundNode.children || [];
    }

    this.selectedFolder = selected;
  }

  loadFolders() {
    const selectedLib = this.foldersSvc.getSelectedLibrary();
    const filtered = (this.selectedStatus === 'All' || selectedLib === this.constants.indexSDM)
      ? this.foldersData
      : this.foldersData.filter(f => f.proceedingStatus === this.selectedStatus);

    this.foldersTree = filtered.map((f: FolderModel) => this.transformToTreeNode(f));
    // this.foldersTree = this.foldersData.map((f: FolderModel) => this.transformToTreeNode(f));
  }

  loadSubFolders(event: any): void {
    const parentNode = event.node;
    parentNode.children = [];

    let selectedItem: FolderModel = {
      name: parentNode.label,
      path: parentNode.key,
      libraryId: this.foldersSvc.getSelectedLibrary()
    };

    // Get the subfolders for the expanded node.
    this.foldersSvc.getSubFolders(selectedItem).subscribe((subs: FolderModel[]) => {
      parentNode.children = subs.map((f: FolderModel) => this.transformToTreeNode(f));
      // this.expandNextTreeNode(parentNode.children);
    });
  }

  transformToTreeNode(folder: FolderModel): TreeNode {
    let node = {
      label: folder.name,
      data: folder.proceedingStatus,
      key: folder.path,
      leaf: !folder.hasChildren,
      children: folder.hasChildren ? [] : undefined
    };

    return node;
  }

  selectNode(event: TreeNodeSelectEvent) {
    const node = event.node;
    this.selectedFolder = node;
    if (node.key) {
      this.foldersSvc.setSelectedFolder(node.key);
    }
  }

  ensurePath(path: string) {
    if (!path.startsWith('/')) {
      path = "/" + path;
    }

    return path;
  }

  goToAdvSearch() {
    let folderPath = '';
    if (this.selectedFolder?.key) {
      folderPath = this.ensurePath(this.selectedFolder?.key);
    }

    let textOp = this.constants.text_operator_equals;

    //get the property Item Id
    let i = this.mdItems?.find((m) => m.cogSearchKey == 'FoldersFiledIn') as MetadataProp;
    let pid = '';
    if (i) {
      pid = i.id;
    }

    let searchSetupData: SearchComboItemModel = {
      isDate: false,
      displayName: "Folders Filed In",
      propertyItem: pid,
      textOperator: textOp,
      keywords: folderPath
    };

    let p = this.formsSvc.updateProp(searchSetupData);

    const allArray = this.searchForm.get(this.constants.property_operator_all) as FormArray;
    allArray.clear();  // Clear the other items since we are using a pre-canned query.
    allArray.push(p);

    this.searchForm.controls["indexId"].setValue(this.selectedLib);
    // Update the form values in the query state service which drives functions
    this.queryStateSvc.updateFormData(this.searchForm.value); // Will update/create the Search request in the Query Service.

    // update the search form then go to Adv Search
    this.router.navigate(['/adv-search']);
  }

  updateProcedings(status: any) {
    this.selectedStatus = status.value;
    this.loadFolders();
  }
}
